import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";

import {View} from "@khanacademy/wonder-blocks-core";
import {CheckboxGroup, Choice} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingSmall,
    HeadingXSmall,
} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {mediaQueries} from "@khan/shared-styles";
import {useLocation} from "@khan/urls";

import {getValidContentKinds, getValidTopicAncestors} from "./constants.ts";

type Props = {
    /** When multiple filters are present, the connection between checkboxes and labels can cause
     * accessibility issues. This identifier is used to create unique IDs for each checkbox and
     * label pair.
     */
    identifier: string;
    /** The current content kinds selected by the user. */
    contentKinds: string[];
    /** A function to update the content kinds selected by the user. */
    setContentKinds: (contentKinds: string[]) => void;
    /** The current domains selected by the user. */
    domains: string[];
    /** A function to update the domains selected by the user. */
    setDomains: (domains: string[]) => void;
    /** A function to update the page number. */
    setPageNumber: (pageNum: number) => void;
};

const isVisible = ({isVisible}: {isVisible?: boolean}) => isVisible !== false;

const Filters = ({
    identifier,
    contentKinds,
    setContentKinds,
    domains,
    setDomains,
    setPageNumber,
}: Props) => {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const query = useLocation().queryParams.page_search_query;
    return (
        <View style={styles.navColumnContent}>
            <HeadingSmall style={styles.navColumnHeader} tag="h2">
                {t`Refine your search`}
            </HeadingSmall>
            <CheckboxGroup
                label={
                    <HeadingXSmall style={styles.navColumnSubheader} tag="h3">
                        {t`Content type`}
                    </HeadingXSmall>
                }
                selectedValues={contentKinds}
                onChange={(selection) => {
                    publishAnalyticsEvent({
                        eventType: "SearchContentTypeFilterSelectedTI",
                        eventSchemaVersion: 1,
                        searchTerm: query,
                        contentType: selection.join(","),
                    });
                    setContentKinds(selection);
                    setPageNumber(1);
                }}
                groupName={`${identifier}-search-filter-content-kind`}
            >
                {getValidContentKinds().map((kind, index) => {
                    return (
                        <Choice
                            label={kind.label}
                            id={kind.id}
                            value={kind.id}
                            key={`${identifier}-search-filter-content-kind-key-${index}`}
                        />
                    );
                })}
            </CheckboxGroup>
            <Strut size={spacing.xLarge_32} />
            <CheckboxGroup
                label={
                    <HeadingXSmall style={styles.navColumnSubheader} tag="h3">
                        {t`Domain`}
                    </HeadingXSmall>
                }
                selectedValues={domains}
                onChange={(selection) => {
                    publishAnalyticsEvent({
                        eventType: "SearchDomainFilterSelectedTI",
                        eventSchemaVersion: 1,
                        searchTerm: query,
                        domain: selection.join(","),
                    });
                    setDomains(selection);
                    setPageNumber(1);
                }}
                groupName={`${identifier}-search-filter-domain`}
            >
                {getValidTopicAncestors()
                    .filter(isVisible)
                    .map(({id, label}) => {
                        return (
                            <Choice
                                label={label}
                                value={id}
                                key={`${identifier}-search-filter-domain-${id}`}
                            />
                        );
                    })}
            </CheckboxGroup>
        </View>
    );
};

const styles = StyleSheet.create({
    navColumnHeader: {
        marginBottom: spacing.large_24,
        [mediaQueries.xsOrSmaller]: {
            display: "none",
        },
    },
    navColumnSubheader: {
        color: color.offBlack64,
    },
    navColumnContent: {
        flex: "1 1 auto",
        paddingLeft: spacing.xSmall_8,
        paddingRight: spacing.xSmall_8,

        // Tablet & desktop view: two-column layout
        [mediaQueries.smOrLarger]: {
            paddingTop: spacing.small_12,
            paddingBottom: spacing.small_12,
        },
        [mediaQueries.xsOrSmaller]: {
            padding: spacing.xxSmall_6,
        },
    },
});

export default Filters;
